import {
  cdr,
  formatDuration,
  useAuth,
  type AnalyticsBucket,
  type AnalyticsResponse,
} from "@savvy/runtime";
import { Button, Card, cn } from "@savvy/ui";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Analytics: KPI tiles, call volume by outcome (stacked columns), service
 * level over time (line), and the data table.
 *
 * Everything is aggregated server-side by the cdr.calls_15m continuous
 * aggregate; this view only divides. Inbound only on purpose — analytics is
 * about the call center's callers, and outbound/consult legs would pollute
 * every rate.
 *
 * Series colors are the validated categorical slots (dataviz palette,
 * re-validated against THIS app's surfaces, both modes). Light-mode orange
 * and aqua sit below 3:1 contrast on the card surface — the relief rule is
 * satisfied by the data table below the charts.
 */

const SLA_SEC = "30"; // service-level threshold; the API's cumulative buckets make this a constant, not a query

type Preset = { key: string; label: string; hours: number; bucket: "15m" | "1h" | "1d" };
const PRESETS: Preset[] = [
  { key: "today", label: "Today", hours: 24, bucket: "15m" },
  { key: "7d", label: "Last 7 days", hours: 7 * 24, bucket: "1h" },
  { key: "30d", label: "Last 30 days", hours: 30 * 24, bucket: "1d" },
];

export function AnalyticsView() {
  const api = useAuth((s) => s.api);
  const [preset, setPreset] = useState<Preset>(PRESETS[0]!);
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (p: Preset) => {
    setLoading(true);
    setErr(null);
    try {
      const now = new Date();
      const from = p.key === "today"
        ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
        : new Date(now.getTime() - p.hours * 3600_000);
      const res = await cdr(api).analytics({
        from: from.toISOString(),
        to: now.toISOString(),
        bucket: p.bucket,
        direction: "inbound",
      });
      setData(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api]);

  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    void load(PRESETS[0]!);
  }, [load]);

  const pick = (p: Preset) => {
    setPreset(p);
    void load(p);
  };

  const t = data?.totals;
  const slaPct = t ? serviceLevel(t) : null;

  return (
    <div>
      {/* One filter row above everything it scopes. */}
      <div className="mb-3 flex items-center gap-1.5">
        {PRESETS.map((p) => (
          <Button
            key={p.key}
            variant={p.key === preset.key ? "default" : "outline"}
            size="sm"
            onClick={() => pick(p)}
          >
            {p.label}
          </Button>
        ))}
        <span className="text-sm text-muted-foreground">inbound calls · {preset.bucket} buckets</span>
      </div>
      {err && <p className="rounded-md bg-danger-bg px-2.5 py-1.5 text-sm text-danger">{err}</p>}

      {/* Refetch keeps the frame: previous render at reduced opacity. */}
      <div className={cn(loading && "opacity-55")}>
        {t && (
          <div className="mb-3 grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-3">
            <Tile label="Calls" value={compact(t.calls)} />
            <Tile label="Answered" value={compact(t.answered)} sub={pct(t.answered, t.calls)} />
            <Tile label="Abandoned" value={compact(t.abandoned)} sub={pct(t.abandoned, t.calls)} />
            <Tile
              label={`Service level (≤${SLA_SEC}s)`}
              value={slaPct === null ? "—" : `${slaPct.toFixed(0)}%`}
              sub="short abandons excluded"
            />
            <Tile
              label="Avg speed of answer"
              value={t.answered ? formatDuration(Math.round(t.wait_answered_sec_sum / t.answered)) : "—"}
            />
            <Tile
              label="Avg talk time"
              value={t.answered ? formatDuration(Math.round(t.talk_sec_sum / t.answered)) : "—"}
            />
          </div>
        )}

        {data && data.buckets.length === 0 && (
          <p className="text-muted-foreground">No inbound calls in this range.</p>
        )}
        {data && data.buckets.length > 0 && (
          <>
            <Card className="mb-3 pb-2">
              <h2 className="mb-1.5 text-sm font-semibold">Call volume by outcome</h2>
              <VolumeChart data={data} />
            </Card>
            <Card className="mb-3 pb-2">
              <h2 className="mb-1.5 text-sm font-semibold">Service level (≤{SLA_SEC}s, per bucket)</h2>
              <ServiceLevelChart data={data} />
            </Card>
            {/* The table view: the relief for sub-3:1 light-mode fills, and
                the no-hover path to every value a tooltip shows. */}
            <details>
              <summary className="mb-1.5 cursor-pointer text-muted-foreground">Data table</summary>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {["Bucket", "Calls", "Answered", "Abandoned", "No agent", `SL ≤${SLA_SEC}s`, "ASA"].map((h) => (
                      <th key={h} className="border-b px-2.5 py-1.5 text-left text-[0.7rem] font-medium uppercase text-muted-foreground">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.buckets.map((b) => {
                    const sl = serviceLevel(b);
                    const td = "border-b px-2.5 py-1.5 text-left";
                    return (
                      <tr key={b.bucket}>
                        <td className={cn(td, "font-mono tabular-nums")}>
                          {bucketLabel(b.bucket, data.bucket_seconds)}
                        </td>
                        <td className={td}>{b.calls}</td>
                        <td className={td}>{b.answered}</td>
                        <td className={td}>{b.abandoned}</td>
                        <td className={td}>{b.no_agent}</td>
                        <td className={td}>{sl === null ? "—" : `${sl.toFixed(0)}%`}</td>
                        <td className={td}>
                          {b.answered ? formatDuration(Math.round(b.wait_answered_sec_sum / b.answered)) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

/** Standard formula: answered within T over (answered + abandons that waited
 *  LONGER than T) — a caller who left before the SLA clock ran out is excluded
 *  from the denominator. Null when nothing qualifies. */
function serviceLevel(b: AnalyticsBucket): number | null {
  const denom = b.answered + b.abandoned - (b.abandoned_within[SLA_SEC] ?? 0);
  if (denom <= 0) return null;
  return (100 * (b.answered_within[SLA_SEC] ?? 0)) / denom;
}

function pct(part: number, whole: number): string {
  return whole > 0 ? `${((100 * part) / whole).toFixed(0)}%` : "";
}

function compact(n: number): string {
  return n >= 10000 ? `${(n / 1000).toFixed(1)}K` : n.toLocaleString();
}

function bucketLabel(iso: string, bucketSec: number): string {
  const d = new Date(iso);
  if (bucketSec >= 86400) {
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border bg-card px-3 py-2 text-card-foreground">
      <span className="text-[0.72rem] uppercase text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold">{value}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

// ── charts (plain SVG; colors come from the validated --viz-* tokens) ───────

const W = 720;
const H = 220;
const PAD = { l: 44, r: 12, t: 8, b: 22 };
const PW = W - PAD.l - PAD.r;
const PH = H - PAD.t - PAD.b;

const SERIES = [
  { key: "answered", label: "Answered", token: "var(--viz-s1)" },
  { key: "abandoned", label: "Abandoned", token: "var(--viz-s2)" },
  { key: "no_agent", label: "No agent", token: "var(--viz-s3)" },
] as const;

/** Clean y-axis maximum: 1/2/5 Ã— 10^k at or above the data max. */
function niceMax(v: number): number {
  if (v <= 4) return 4;
  const mag = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5, 10]) {
    if (m * mag >= v) return m * mag;
  }
  return 10 * mag;
}

/** Sparse x ticks: ~6, always including first and last. */
function tickIndexes(n: number): number[] {
  if (n <= 6) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil(n / 6);
  const out: number[] = [];
  for (let i = 0; i < n; i += step) out.push(i);
  if (out[out.length - 1] !== n - 1) out.push(n - 1);
  return out;
}

function VolumeChart({ data }: { data: AnalyticsResponse }) {
  const [hover, setHover] = useState<number | null>(null);
  const buckets = data.buckets;
  const n = buckets.length;
  const yMax = niceMax(Math.max(...buckets.map((b) => b.answered + b.abandoned + b.no_agent)));
  const band = PW / n;
  const barW = Math.min(24, Math.max(2, band - 4)); // ≤24px thick; the band's leftover is air
  const y = (v: number) => PAD.t + PH - (v / yMax) * PH;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Call volume by outcome over time" className="block h-auto w-full">
        {/* recessive hairline grid + clean ticks */}
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(yMax * f)} y2={y(yMax * f)} className="stroke-border" strokeWidth={1} />
            <text x={PAD.l - 6} y={y(yMax * f) + 3} className="fill-muted-foreground text-[10px] tabular-nums" textAnchor="end">
              {(yMax * f).toLocaleString()}
            </text>
          </g>
        ))}
        <line x1={PAD.l} x2={W - PAD.r} y1={y(0)} y2={y(0)} className="stroke-muted-foreground" strokeWidth={1} />

        {buckets.map((b, i) => {
          const cx = PAD.l + band * i + band / 2;
          const x = cx - barW / 2;
          // Stack answered → abandoned → no_agent from the baseline, a 2px
          // surface gap between touching segments; only the TOP of the whole
          // column gets the 4px rounded data-end (baseline stays square).
          const values = SERIES.map((s) => b[s.key]);
          const segs: { color: string; y0: number; y1: number }[] = [];
          let acc = 0;
          values.forEach((v, si) => {
            if (v > 0) {
              segs.push({ color: SERIES[si]!.token, y0: y(acc + v), y1: y(acc) });
              acc += v;
            }
          });
          return (
            <g key={b.bucket} opacity={hover === null || hover === i ? 1 : 0.55}>
              {segs.map((s, si) => {
                const isTop = si === segs.length - 1;
                const gap = si > 0 ? 2 : 0; // surface gap between segments
                const h = Math.max(0, s.y1 - s.y0 - gap);
                const top = s.y0 + gap;
                const r = isTop ? Math.min(4, h / 2, barW / 2) : 0;
                return (
                  <path
                    key={si}
                    d={roundedTopRect(x, top, barW, h, r)}
                    fill={s.color}
                  />
                );
              })}
              {/* hit target: the whole band, bigger than the mark */}
              <rect
                x={PAD.l + band * i}
                y={PAD.t}
                width={band}
                height={PH}
                fill="transparent"
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover(null)}
              />
            </g>
          );
        })}

        {tickIndexes(n).map((i) => (
          <text
            key={i}
            x={PAD.l + band * i + band / 2}
            y={H - 6}
            className="fill-muted-foreground text-[10px] tabular-nums"
            textAnchor="middle"
          >
            {bucketLabel(buckets[i]!.bucket, data.bucket_seconds)}
          </text>
        ))}
      </svg>

      {hover !== null && buckets[hover] && (
        <div className={tooltipBox} style={{ left: `${((PAD.l + band * hover + band / 2) / W) * 100}%` }}>
          <div className="mb-0.5 text-muted-foreground">{bucketLabel(buckets[hover].bucket, data.bucket_seconds)}</div>
          {SERIES.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5">
              <span className="inline-block h-[3px] w-2.5 rounded-[1px]" style={{ background: s.token }} />
              <strong>{buckets[hover]![s.key]}</strong>
              <span className="text-muted-foreground">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* legend: identity never rides on color-matching alone */}
      <div className="flex gap-4 pb-1 pt-1.5 text-xs text-muted-foreground">
        {SERIES.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm" style={{ background: s.token }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The hover readout: values lead, labels follow (dataviz interaction spec). */
const tooltipBox =
  "pointer-events-none absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-md border bg-background px-2 py-1 text-xs text-foreground";

/** Rect with only the top corners rounded — data-end round, baseline square. */
function roundedTopRect(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return "";
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

function ServiceLevelChart({ data }: { data: AnalyticsResponse }) {
  const [hover, setHover] = useState<number | null>(null);
  const buckets = data.buckets;
  const n = buckets.length;
  const band = PW / n;
  const pts = buckets.map((b, i) => ({
    x: PAD.l + band * i + band / 2,
    sl: serviceLevel(b),
  }));
  const y = (v: number) => PAD.t + PH - (v / 100) * PH;

  // The line skips buckets with no qualifying calls rather than drawing 0.
  const path = pts.reduce((d, p) => {
    if (p.sl === null) return d;
    return d + (d === "" ? "M" : "L") + `${p.x},${y(p.sl)}`;
  }, "");

  const hoverPt = hover !== null ? pts[hover] : null;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Service level per bucket"
        className="block h-auto w-full"
        onPointerMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * W;
          const i = Math.min(n - 1, Math.max(0, Math.floor((px - PAD.l) / band)));
          setHover(i);
        }}
        onPointerLeave={() => setHover(null)}
      >
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line
              x1={PAD.l}
              x2={W - PAD.r}
              y1={y(v)}
              y2={y(v)}
              className={v === 0 ? "stroke-muted-foreground" : "stroke-border"}
              strokeWidth={1}
            />
            <text x={PAD.l - 6} y={y(v) + 3} className="fill-muted-foreground text-[10px] tabular-nums" textAnchor="end">
              {v}%
            </text>
          </g>
        ))}

        {/* crosshair finds the X */}
        {hoverPt && (
          <line
            x1={hoverPt.x}
            x2={hoverPt.x}
            y1={PAD.t}
            y2={PAD.t + PH}
            className="stroke-muted-foreground"
            strokeWidth={1}
          />
        )}

        <path d={path} fill="none" stroke="var(--viz-s1)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* end dot with a 2px surface ring */}
        {(() => {
          const last = [...pts].reverse().find((p) => p.sl !== null);
          if (!last || last.sl === null) return null;
          return (
            <circle cx={last.x} cy={y(last.sl)} r={4} fill="var(--viz-s1)" stroke="var(--card)" strokeWidth="2" />
          );
        })()}

        {hoverPt && hoverPt.sl !== null && (
          <circle cx={hoverPt.x} cy={y(hoverPt.sl)} r={4} fill="var(--viz-s1)" stroke="var(--card)" strokeWidth="2" />
        )}

        {tickIndexes(n).map((i) => (
          <text key={i} x={pts[i]!.x} y={H - 6} className="fill-muted-foreground text-[10px] tabular-nums" textAnchor="middle">
            {bucketLabel(buckets[i]!.bucket, data.bucket_seconds)}
          </text>
        ))}
      </svg>

      {hover !== null && buckets[hover] && (
        <div className={tooltipBox} style={{ left: `${(pts[hover]!.x / W) * 100}%` }}>
          <div className="mb-0.5 text-muted-foreground">{bucketLabel(buckets[hover].bucket, data.bucket_seconds)}</div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-[3px] w-2.5 rounded-[1px]" style={{ background: "var(--viz-s1)" }} />
            <strong>{pts[hover]!.sl === null ? "—" : `${pts[hover]!.sl!.toFixed(0)}%`}</strong>
            <span className="text-muted-foreground">service level</span>
          </div>
        </div>
      )}
    </div>
  );
}
